import { ThemeColors } from '@/types/theme';

// Helper function to convert hex to HSL
function hexToHsl(hex: string): { h: number; s: number; l: number } {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return { h: 0, s: 0, l: 0 };
  
  const r = parseInt(result[1], 16) / 255;
  const g = parseInt(result[2], 16) / 255;
  const b = parseInt(result[3], 16) / 255;
  
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h: number, s: number;
  const l = (max + min) / 2;
  
  if (max === min) {
    h = s = 0; // achromatic
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
      default: h = 0;
    }
    h /= 6;
  }
  
  return { h: h * 360, s: s * 100, l: l * 100 };
}

// Helper function to convert HSL to hex
function hslToHex(h: number, s: number, l: number): string {
  h = h % 360;
  s = s / 100;
  l = l / 100;
  
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs((h / 60) % 2 - 1));
  const m = l - c / 2;
  
  let r = 0, g = 0, b = 0;
  
  if (0 <= h && h < 60) {
    r = c; g = x; b = 0;
  } else if (60 <= h && h < 120) {
    r = x; g = c; b = 0;
  } else if (120 <= h && h < 180) {
    r = 0; g = c; b = x;
  } else if (180 <= h && h < 240) {
    r = 0; g = x; b = c;
  } else if (240 <= h && h < 300) {
    r = x; g = 0; b = c;
  } else if (300 <= h && h < 360) {
    r = c; g = 0; b = x;
  }
  
  r = Math.round((r + m) * 255);
  g = Math.round((g + m) * 255);
  b = Math.round((b + m) * 255);
  
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
}

// Helper function to adjust lightness while preserving hue and saturation
function adjustLightness(color: string, adjustment: number): string {
  const hsl = hexToHsl(color);
  const newLightness = Math.max(0, Math.min(100, hsl.l + adjustment));
  return hslToHex(hsl.h, hsl.s, newLightness);
}

// Helper function to get a contrasting color for text
function getContrastingColor(bgColor: string, darkText = '#1f2937', lightText = '#f9fafb'): string {
  const hsl = hexToHsl(bgColor);
  return hsl.l > 50 ? darkText : lightText;
}

// Main function to generate dark mode version of any theme
export function generateDarkModeColors(lightColors: ThemeColors): ThemeColors {
  // For brand colors, ensure they're visible on dark backgrounds
  const primaryHsl = hexToHsl(lightColors.primary);
  const secondaryHsl = hexToHsl(lightColors.secondary);
  
  // Brighten brand colors if they're too dark for dark mode
  const darkModePrimary = primaryHsl.l < 50 ? adjustLightness(lightColors.primary, 20) : lightColors.primary;
  const darkModeSecondary = secondaryHsl.l < 50 ? adjustLightness(lightColors.secondary, 20) : lightColors.secondary;
  
  return {
    // Adjust primary colors for dark mode visibility
    primary: darkModePrimary,
    primaryHover: adjustLightness(darkModePrimary, 10),
    primaryLight: adjustLightness(darkModePrimary, -20),
    primaryForeground: '#000000', // Dark text on bright primary
    
    // Adjust secondary colors
    secondary: darkModeSecondary,
    secondaryHover: adjustLightness(darkModeSecondary, 10),
    secondaryForeground: '#000000', // Dark text on bright secondary
    
    // Accent colors - make them more vibrant for dark mode
    accent: '#1e293b', // Dark accent backgrounds matching cards
    accentHover: '#334155', // Lighter on hover
    accentForeground: '#f1f5f9', // Light text on accent
    
    // Dark backgrounds with proper contrast
    background: '#0f172a', // Dark slate
    foreground: '#f1f5f9', // Very light text
    
    // Muted colors for dark mode
    muted: '#0f172a', // Very dark muted background for stage columns
    mutedForeground: '#e2e8f0', // Even lighter muted text for better contrast
    
    // Borders and inputs
    border: '#64748b', // Lighter border for better card definition
    input: '#1e293b', // Dark input background
    ring: darkModePrimary, // Use adjusted brand color
    
    // Destructive colors (keep visible in dark mode)
    destructive: '#f87171', // Lighter red for dark mode
    destructiveForeground: '#000000', // Dark text on light red
    
    // Cards with better contrast
    card: '#1e293b', // Dark card background
    cardForeground: '#f1f5f9', // Very light card text
    
    // Popovers
    popover: '#1e293b', // Dark popover background
    popoverForeground: '#f1f5f9', // Very light popover text
    
    // Sidebar colors
    sidebarColor: '#0f172a', // Very dark sidebar
    sidebarTextColor: '#e2e8f0', // Light sidebar text
    sidebarHoverBgColor: '#334155', // Hover background
    sidebarHoverTextColor: '#ffffff', // White hover text
  };
}

// Function to apply dark mode adjustments to any theme
export function applyDarkModeToTheme(colors: ThemeColors, isDark: boolean): ThemeColors {
  if (!isDark) {
    return colors; // Return original colors for light mode
  }
  
  return generateDarkModeColors(colors);
}